import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { ExploreService } from "../../explore/explore.service";
import { AnswerMetadata } from "./coutry-pages.service";

@Injectable({
  providedIn: 'root'
})

export class DataShareService {
  countryCode: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  /**
   * Code used only for display (flag/label), kept separate from `countryCode` (the data code).
   * They differ for the Global default preview, where data is borrowed from GLOBAL_DATA_COUNTRY
   * (FR) but the UI must still show GLOBAL_SCOPE_CODE (EU). For real countries the two match.
   */
  displayCountryCode: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  countryName: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  surveyAnswers: BehaviorSubject<Object[]> = new BehaviorSubject<Object[]>([null, null]);
  countrySurveyAnswer: BehaviorSubject<Object | null> = new BehaviorSubject<Object | null>(null);
  countrySurveyAnswerMetaData: BehaviorSubject<AnswerMetadata | null> = new BehaviorSubject<AnswerMetadata | null>(null);
  year: BehaviorSubject<string> = new BehaviorSubject<string>('2024');

  countryCode$ = this.countryCode.asObservable();
  displayCountryCode$ = this.displayCountryCode.asObservable();
  year$ = this.year.asObservable();

  constructor(private exploreService: ExploreService) {}

  /**
   * Updates a specific item in the surveyAnswers array at the given index
   * @param index - The position in the array where the item should be updated
   * @param item - The new item to be placed at the specified index
   */
  setItemAt(index: number, item: Object) {
    const current = [...this.surveyAnswers.getValue()];
    current[index] = item;
    this.surveyAnswers.next(current);
  }


  calculateDiff(previous: number | null, next: number | null): number | null {
    // Check empty or non-computable values
    if (
      previous === null || next === null ||
      previous === undefined || next === undefined ||
      Number.isNaN(previous) || Number.isNaN(next)
    ) {
      return null;
    }

    return (next - previous);
  }

  /**
   * Calculates the percentage of a value relative to a total
   * @param value - The numeric value to calculate percentage for (as string)
   * @param total - The total value to calculate percentage against (as string)
   * @returns Rounded percentage value or null if inputs are invalid or total is zero
   */
  calculatePercentage(value: string, total: string): number | null {

    if (!this.exploreService.isNumeric(value) || !this.exploreService.isNumeric(total) || +total === 0) {
      return null;
    }

    return Math.round((+value / +total + Number.EPSILON) * 100);
  }

  /**
   * Calculates the year-over-year percentage change of `next` relative to `previous`
   * (standard percent change: (next - previous) / previous).
   * @param previous - The previous year's value (as string)
   * @param next - The current year's value (as string)
   * @returns Rounded percentage change, or null if inputs are invalid or previous is zero
   */
  calculateDiffAsPercentage(previous: string | null, next: string | null): number | null {

    if (!this.exploreService.isNumeric(previous) || !this.exploreService.isNumeric(next)) {
      return null;
    }

    const prev = +previous;
    const curr = +next;

    // Percentage change is undefined when the base (previous) is zero.
    if (prev === 0) {
      return null;
    }

    return Math.round(((curr - prev) / prev) * 100);
  }


  /**checks if there is at least one value that is not null or undefined */
  hasAnyValue(values: any[]): boolean {
    return values.some(value => value != null);
  }

  /** Checks if there is available data for at least one of the specified questions.
   *
   * If a question's value is an array, it looks for at least one element
   * with a truthy value in the `${q}-1` key. If it's a simple value, it checks if it's truthy.
   */
  hasSurveyData(data: any, questions: string[]): boolean {
    if (!data) {
      return false;
    }

    return questions.some(q => {
      if (Array.isArray(data[q])) {
        return data[q].some((item: any) => !!item[`${q}-1`]);
      } else {
        return !!data[q];
      }
    });
  }
}
